import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import {Breadcrumb} from '../../components/ui/Breadcrumb';
import EventHeader from './components/EventHeader';
import ExpenseList from './components/ExpenseList';
import BalanceSummary from './components/BalanceSummary';
import SettlementHistory from './components/SettlementHistory';
import AddExpenseModal from './components/AddExpenseModal';
import AddParticipantModal from './components/AddParticipantModal';
import ParticipantManagementModal from './components/ParticipantManagementModal';
import SettleUpModal from './components/SettleUpModal';
import apiService from '../../services/api';

const EventDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [balances, setBalances] = useState([]);
  const [settlements, setSettlements] = useState([]);
  const [isAddExpenseModalOpen, setIsAddExpenseModalOpen] = useState(false);
  const [isAddParticipantModalOpen, setIsAddParticipantModalOpen] = useState(false);
  const [isParticipantManagementModalOpen, setIsParticipantManagementModalOpen] = useState(false);
  const [isSettleUpModalOpen, setIsSettleUpModalOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

   // Calculate balances
  const calculateBalances = (eventData, settlements = []) => {
    const participantBalances = {};
    
    // Initialize balances
    eventData?.participants?.forEach(participant => {
      participantBalances[participant.id] = {
        participant,
        paid: 0,
        owes: 0,
        amount: 0
      };
    });

    // Calculate what each person paid and owes from expenses
    eventData?.expenses?.forEach(expense => {
      // Add to what the payer paid
      if (participantBalances[expense.paidBy.id]) {
        participantBalances[expense.paidBy.id].paid += expense?.amount;
      }

      // Calculate what each person owes
      if (expense?.splitType === 'equal') {
        // For equal splits, split among all participants
        const splitAmount = expense?.amount / eventData?.participants?.length;
        eventData?.participants?.forEach(participant => {
          if (participantBalances?.[participant.id]) {
            participantBalances[participant.id].owes += splitAmount;
          }
        });
      } else if (expense?.splitType === 'custom' && expense?.customSplits) {
        // For custom splits, use the specified amounts
        Object.entries(expense?.customSplits)?.forEach(([participantId, amount]) => {
          if (participantBalances?.[participantId]) {
            participantBalances[participantId].owes += amount;
          }
        });
      }
    });

    // Calculate net balance (positive = gets money back, negative = owes money)
    Object.keys(participantBalances)?.forEach(participantId => {
      const balance = participantBalances?.[participantId];
      balance.amount = balance?.paid - balance?.owes;
    });

    // Adjust balances based on settlements
    // When a settlement occurs (paidBy pays paidTo), it reduces the overall debt/credit with the group
    settlements?.forEach(settlement => {
      // Convert settlement amount to number to ensure proper calculation
      const amount = parseFloat(settlement.amount);
      
      // paidBy was owed money by the group (positive balance) or owed money to the group (negative balance)
      // When they make a payment, their balance with the group changes
      if (participantBalances[settlement.paidBy]) {
        // If paidBy had positive balance (was owed money), paying reduces that
        // If paidBy had negative balance (owed money), paying reduces their debt (increases balance toward 0)
        participantBalances[settlement.paidBy].amount += amount;
      }
      
      // paidTo was owed money by the group (positive balance) or owed money to the group (negative balance)  
      // When they receive a payment, their balance with the group changes
      if (participantBalances[settlement.paidTo]) {
        // If paidTo had positive balance (was owed money), receiving reduces their credit from the group
        // If paidTo had negative balance (owed money), receiving increases their credit (decreases balance toward 0)
        participantBalances[settlement.paidTo].amount -= amount;
      }
    });

    return Object.values(participantBalances);
  };

  useEffect(() => {
    const loadEvent = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        const response = await apiService.getEventDetails(id);
        if (response.success) {
          const eventResponse = response.data.event;
          
          // Get settlements for the event to adjust balances
          const settlementsResponse = await apiService.getEventSettlements(id);
          const settlements = settlementsResponse.success ? settlementsResponse.data.settlements : [];
          
          setEvent(eventResponse);
          setBalances(calculateBalances(eventResponse, settlements));
          setSettlements(settlements);
        } else {
          setError(response.message || 'Failed to load event details');
        }
      } catch (err) {
        console.error('Error fetching event details:', err);
        setError(err.message || 'Failed to load event details');
      } finally {
        setIsLoading(false);
      }
    };

    loadEvent();
  }, [id]);

  const handleAddExpense = async (expenseData) => {
    try {
      // Refresh the event data from the API to get the updated expenses
      const response = await apiService.getEventDetails(id);
      if (response.success) {
        const eventResponse = response.data.event;
        
        // Get settlements for the event to adjust balances
        const settlementsResponse = await apiService.getEventSettlements(id);
        const settlements = settlementsResponse.success ? settlementsResponse.data.settlements : [];
        
        setEvent(eventResponse);
        setBalances(calculateBalances(eventResponse, settlements));
        setSettlements(settlements);
      }
      setEditingExpense(null);
      setIsAddExpenseModalOpen(false);
    } catch (error) {
      console.error('Error adding/updating expense:', error);
      alert(error.message || 'Error adding/updating expense');
    }
  };

  const handleEditExpense = (expense) => {
    setEditingExpense(expense);
    setIsAddExpenseModalOpen(true);
  };

  const handleDeleteExpense = async (expense) => {
    if (window.confirm('Are you sure you want to delete this expense?')) {
      try {
        // Delete expense via API
        await apiService.deleteExpense(expense.id);
        // Refresh the event data from the API to get the updated expenses
        const response = await apiService.getEventDetails(id);
        if (response.success) {
          const eventResponse = response.data.event;
          
          // Get settlements for the event to adjust balances
          const settlementsResponse = await apiService.getEventSettlements(id);
          const settlements = settlementsResponse.success ? settlementsResponse.data.settlements : [];
          
          setEvent(eventResponse);
          setBalances(calculateBalances(eventResponse, settlements));
          setSettlements(settlements);
        }
      } catch (error) {
        console.error('Error deleting expense:', error);
        alert(error.message || 'Error deleting expense');
      }
    }
  };

  const handleAddParticipant = async (participantData) => {
    try {
      // Refresh the event data from the API to get the updated participants
      const response = await apiService.getEventDetails(id);
      if (response.success) {
        const eventResponse = response.data.event;
        
        // Get settlements for the event to adjust balances
        const settlementsResponse = await apiService.getEventSettlements(id);
        const settlements = settlementsResponse.success ? settlementsResponse.data.settlements : [];
        
        setEvent(eventResponse);
        setBalances(calculateBalances(eventResponse, settlements));
        setSettlements(settlements);
      }
      setIsAddParticipantModalOpen(false);
    } catch (error) {
      console.error('Error adding participant:', error);
      alert(error.message || 'Error adding participant');
    }
  };

  const handleRemoveParticipant = async (participant) => {
    try {
      // Refresh the event data from the API to get the updated participants
      const response = await apiService.getEventDetails(id);
      if (response.success) {
        const eventResponse = response.data.event;
        
        // Get settlements for the event to adjust balances
        const settlementsResponse = await apiService.getEventSettlements(id);
        const settlements = settlementsResponse.success ? settlementsResponse.data.settlements : [];
        
        setEvent(eventResponse);
        setBalances(calculateBalances(eventResponse, settlements));
        setSettlements(settlements);
      }
    } catch (error) {
      console.error('Error removing participant:', error);
      alert(error.message || 'Error removing participant');
    }
  };

  const handleRecordPayment = async (settlementData) => {
    try {
      // Refresh the event data from the API to get the updated balances
      const response = await apiService.getEventDetails(id);
      if (response.success) {
        const eventResponse = response.data.event;
        
        // Get settlements for the event to adjust balances
        const settlementsResponse = await apiService.getEventSettlements(id);
        const settlements = settlementsResponse.success ? settlementsResponse.data.settlements : [];
        
        setEvent(eventResponse);
        setBalances(calculateBalances(eventResponse, settlements));
        setSettlements(settlements);
      }
      setIsSettleUpModalOpen(false);
    } catch (error) {
      console.error('Error recording payment:', error);
      alert(error.message || 'Error recording payment');
    }
  };

  const handleDeleteSettlement = async (settlement) => {
    if (window.confirm(`Are you sure you want to delete this settlement of ${settlement.amount} from ${settlement.payer?.name} to ${settlement.payee?.name}?`)) {
      try {
        await apiService.deleteSettlement(settlement.id);
        // Refresh the event data from the API to get the updated settlements
        const response = await apiService.getEventDetails(id);
        if (response.success) {
          const eventResponse = response.data.event;
          
          // Get settlements for the event to adjust balances
          const settlementsResponse = await apiService.getEventSettlements(id);
          const settlements = settlementsResponse.success ? settlementsResponse.data.settlements : [];
          
          setEvent(eventResponse);
          setBalances(calculateBalances(eventResponse, settlements));
          setSettlements(settlements);
        }
      } catch (error) {
        console.error('Error deleting settlement:', error);
        alert(error.message || 'Error deleting settlement');
      }
    }
  };

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/dashboard', icon: 'Home' },
    { label: event?.name || 'Event Details', path: `/events/${id}`, icon: 'Calendar' }
  ];

 if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        {/* <Header user={currentUser} isAuthenticated={true} /> */}
        <main className="pt-16">
          <div className="container mx-auto px-4 py-8">
            <div className="flex items-center justify-center min-h-[400px]">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-muted-foreground">Loading event details...</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        {/* <Header user={currentUser} isAuthenticated={true} /> */}
        <main className="pt-16">
          <div className="container mx-auto px-4 py-8">
            <div className="text-center py-12">
              <h1 className="text-2xl font-semibold text-foreground mb-2">Error Loading Event</h1>
              <p className="text-muted-foreground mb-6">{error}</p>
              <button
                onClick={() => navigate('/dashboard')}
                className="px-6 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-micro"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-background">
        {/* <Header user={currentUser} isAuthenticated={true} /> */}
        <main className="pt-16">
          <div className="container mx-auto px-4 py-8">
            <div className="text-center py-12">
              <h1 className="text-2xl font-semibold text-foreground mb-2">Event Not Found</h1>
              <p className="text-muted-foreground mb-6">The event you're looking for doesn't exist or has been deleted.</p>
              <button
                onClick={() => navigate('/dashboard')}
                className="px-6 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-micro"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Define functions to open modals
  const openAddExpenseModal = () => {
    setIsAddExpenseModalOpen(true);
    setEditingExpense(null); // Ensure we're in "add" mode, not edit mode
  };

  const openAddParticipantModal = () => {
    setIsAddParticipantModalOpen(true);
  };

  const openSettleUpModal = () => {
    setIsSettleUpModalOpen(true);
  };

  const openParticipantManagementModal = () => {
    setIsParticipantManagementModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* <Header user={currentUser} isAuthenticated={true} /> */}
      <main className="pt-16">
        <div className="container mx-auto px-4 py-8 max-w-7xl">
          <Breadcrumb customItems={breadcrumbItems} />
          
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="xl:col-span-2 space-y-6">
              <EventHeader
                event={event}
                onAddExpense={openAddExpenseModal}
                onAddParticipant={openAddParticipantModal}
                onManageParticipants={openParticipantManagementModal}
              />
              
              <ExpenseList
                expenses={event?.expenses}
                participants={event?.participants}
                onEditExpense={handleEditExpense}
                onDeleteExpense={handleDeleteExpense}
              />
              
              <SettlementHistory
                settlements={settlements}
                onSettlementDelete={handleDeleteSettlement}
              />
            </div>

            {/* Sidebar */}
            <div className="xl:col-span-1">
              <BalanceSummary
                balances={balances}
                onSettleUp={openSettleUpModal}
              />
            </div>
          </div>
        </div>
      </main>
      {/* Modals */}
      <AddExpenseModal
        isOpen={isAddExpenseModalOpen}
        onClose={() => {
          setIsAddExpenseModalOpen(false);
          setEditingExpense(null);
        }}
        participants={event?.participants}
        onAddExpense={handleAddExpense}
        editingExpense={editingExpense}
        eventId={id}
      />
      <AddParticipantModal
        isOpen={isAddParticipantModalOpen}
        onClose={() => setIsAddParticipantModalOpen(false)}
        onAddParticipant={handleAddParticipant}
        existingParticipants={event?.participants}
        eventId={id}
      />
      <ParticipantManagementModal
        isOpen={isParticipantManagementModalOpen}
        onClose={() => setIsParticipantManagementModalOpen(false)}
        participants={event?.participants}
        onAddParticipant={handleAddParticipant}
        onRemoveParticipant={handleRemoveParticipant}
        eventId={id}
      />
      <SettleUpModal
        isOpen={isSettleUpModalOpen}
        onClose={() => setIsSettleUpModalOpen(false)}
        balances={balances}
        onRecordPayment={handleRecordPayment}
        eventId={id}
      />
    </div>
  );
};

export default EventDetails;
